<template>
  <ADropdown
    :triggers="[trigger]"
    v-model:shown="computedOpen"
    :placement="placement"
    :delay="{ show: 10, hide: 0 }"
    :auto-hide="autoHide"
    :popper-triggers="trigger === 'hover' ? ['hover'] : []"
    :disabled="disabled"
    @auto-hide="onOutsideClick"
    @update:shown="$emit('update:open', $event)"
    no-auto-focus
  >
    <slot name="trigger" />

    <template #popper>
      <div class="popover-menu__popover" :style="{ '--popover-offset': offset }" tabindex="0" @focus="focusTrapped">
        <PopoverMenuCard :data-test-id="$attrs['data-test-id']" :size="size" ref="Card" tabindex="0">
          <slot name="items-list" />
          <slot name="popover" />
          <div tabindex="0" @focus="focusTrapped" />
        </PopoverMenuCard>
      </div>
    </template>
  </ADropdown>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import shortcutMixin from '@shortcuts/shortcutMixin';

/**
 * Generic popover menu
 *
 * Contains the following logic:
 *
 * - on open, focusses the popover to allow tabbing into the controls
 * - when tabbing away from the popover, closes the popover and focusses the previously focussed element, i.e.
 *   restore the original position in the UI
 * - always allow closing via escape (also triggers re-focus)
 * - by default, only use the triggers of floating-vue, but additionally allow binding to :shown directly
 *
 * It expects the trigger to be a button or a text input
 */
export default defineComponent({
  mixins: [shortcutMixin],
  name: 'PopoverMenu',

  inheritAttrs: false,

  props: {
    trigger: {
      type: String,
      default: 'click',
      validator(value) {
        return ['manual', 'click', 'hover', 'focus'].indexOf(value) !== -1;
      },
    },

    placement: {
      type: String,
      default: 'bottom-start',
      validator(value) {
        return (
          [
            'auto',
            'auto-start',
            'auto-end',
            'top',
            'top-start',
            'top-end',
            'right',
            'right-start',
            'right-end',
            'bottom',
            'bottom-start',
            'bottom-end',
            'left',
            'left-start',
            'left-end',
          ].indexOf(value) !== -1
        );
      },
    },

    open: {
      type: Boolean,
      default: null,
    },

    size: {
      type: String,
      default: 'normal',
      validator(value: string) {
        return ['normal', 'fit-content'].indexOf(value) !== -1;
      },
    },

    autoHide: {
      type: Boolean,
      default: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    offset: {
      type: String,
      default: '8px',
    },
    /**
     * Focus the menu element on show so the user can tab into the  interactive elements
     */
    focusOnShow: {
      type: Boolean,
      default: true,
    },
  },
  emits: { 'update:open': null, show: null, hide: null, 'auto-hide': null },
  data(): {
    unbindManualShortcuts: () => void;
    internalOpen: boolean;
    /**
     * non reactive
     */
    _previouslyFocussedElement?: Element;
  } {
    return {
      unbindManualShortcuts: () => {},
      internalOpen: false,
    };
  },
  watch: {
    computedOpen(value) {
      if (value) {
        this._previouslyFocussedElement = document.activeElement;

        /**
         * Bind a custom escape shortcut that closes the popover
         * We cannot use the popover escape listener because it is called after
         * other shortcuts on body
         *
         * We also use manual binding when the popover opens (mixin option would bind them on component created)
         */
        this.unbindManualShortcuts = this.$bindShortcutsManually({
          cancel: ($event) => {
            this.toggleOpen();

            this.$emit('auto-hide', { event: $event, reason: 'escapeKey' });

            this._previouslyFocussedElement?.focus();
          },
        });

        this.$emit('show');

        // Do not focus the popover when the trigger is an element that takes text input
        const { nodeName, type } = this._previouslyFocussedElement as HTMLInputElement;

        if (!this.focusOnShow || (nodeName === 'INPUT' && type !== 'button')) {
          return;
        }

        setTimeout(() => {
          if (this.$refs.Card) {
            this.$refs.Card.$el.focus();
          }
        }, 50);
      } else if (value === false) {
        this.unbindManualShortcuts();

        this.$emit('hide');
      }
    },
  },
  computed: {
    /**
     * computedOpen allows an optional open property.
     * If the property is not set, the state is handled internally
     *
     * If it is set, it is passed to the ATooltip / emitted to the parent component
     */
    computedOpen: {
      get(): boolean {
        return this.open === null ? this.internalOpen : this.open;
      },
      set(newVal: boolean): void {
        if (this.open === null) {
          this.internalOpen = newVal;
        } else {
          this.$emit('update:open', newVal);
        }
      },
    },
  },
  methods: {
    toggleOpen() {
      this.computedOpen = !this.computedOpen;
    },

    onOutsideClick(event: MouseEvent) {
      this.$emit('auto-hide', { event, reason: 'outsideClick' });
    },

    /**
     * Closes the popover and focusses the previously focussed element.
     *
     * Called by the outer container and an extra element at the end of the popover on @focus
     * to keep the focus at a sensible position when the user tabs outside the popover.
     */
    focusTrapped() {
      this.toggleOpen();

      this._previouslyFocussedElement.focus();
    },
  },
});
</script>

<style lang="scss">
.popover[x-placement^='top'] {
  .popover-menu__popover {
    padding-bottom: var(--popover-offset);
  }
}

.popover[x-placement^='bottom'] {
  .popover-menu__popover {
    padding-top: var(--popover-offset);
  }
}

.popover[x-placement^='right'] {
  .popover-menu__popover {
    padding-left: var(--popover-offset);
  }
}

.popover[x-placement^='left'] {
  .popover-menu__popover {
    padding-right: var(--popover-offset);
  }
}
</style>
